import React, { useState, useEffect } from 'react';

interface MessageThinkingProps {
  currentStage?: string;
  stageMessage?: string;
  progress?: number;
  isExpanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
  requirementHistory?: string[]; // Add requirement history prop
  onUpdateRequirementHistory?: (requirement: string) => void; // Add callback for updating history
  trackedPlans?: string[]; // Add tracked plans prop
  onUpdateTrackedPlans?: (plan: string) => void; // Add callback for updating tracked plans
}

const MessageThinking: React.FC<MessageThinkingProps> = ({ 
  currentStage, 
  stageMessage,
  progress,
  isExpanded = true, // Changed default to true (expanded)
  onToggleExpanded,
  requirementHistory = [],
  onUpdateRequirementHistory,
  trackedPlans = [],
  onUpdateTrackedPlans
}) => {
  const [expanded, setExpanded] = useState(isExpanded);

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  // Update requirement history when stage message changes
  useEffect(() => {
    if (stageMessage && onUpdateRequirementHistory) {
      const currentRequirement = parseCurrentRequirement(stageMessage);
      const enrichmentRequirements = parseEnrichmentRequirements(stageMessage);
      
      // Track plan requirements when they are identified
      if (currentRequirement && stageMessage.includes('Plan:') && onUpdateTrackedPlans) {
        if (!trackedPlans.includes(currentRequirement)) {
          onUpdateTrackedPlans(currentRequirement);
        }
      }
      
      // Handle enrichment requirements specially to avoid duplication
      if (enrichmentRequirements.length > 0) {
        // If we have individual enrichment requirements, only add those (not the general statement)
        // But only add them if they're not already in the current stage (to avoid immediate duplication)
        enrichmentRequirements.forEach(enrichmentReq => {
          if (enrichmentReq && !requirementHistory.includes(enrichmentReq)) {
            onUpdateRequirementHistory(enrichmentReq);
          }
        });
      } else {
        // Add current requirement to history if it's valid and not already present
        if (currentRequirement && !requirementHistory.includes(currentRequirement)) {
          onUpdateRequirementHistory(currentRequirement);
        }
      }
    }
  }, [stageMessage, onUpdateRequirementHistory, requirementHistory, onUpdateTrackedPlans, trackedPlans]);

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggleExpanded?.(newExpanded);
  };

  // Parse current requirement from stage message
  const parseCurrentRequirement = (message: string): string | null => {
    // Handle combined plan messages with planning info
    if (message.includes('Plan:')) {
      // Extract just the plan part (before the | separator if it exists)
      const match = message.match(/Plan: (.+?)(?:\s+\|\s+Planning to fetch|$)/);
      return match ? match[1] : null;
    }
    
    // Handle knowledge requirements
    if (message.includes('Gathering knowledge:')) {
      const match = message.match(/Gathering knowledge: (.+?) (?:\(\d+\/\d+\)|\(\d+s\))/);
      return match ? match[1] : null;
    }
    
    // Handle data requirements - primary
    if (message.includes('Analyzing primary data:')) {
      const match = message.match(/Analyzing primary data: (.+?)(?:\s+\(\d+s\))?$/);
      return match ? `Primary: ${match[1]}` : null;
    }
    
    // Handle data requirements - enrichment parallel (only show this during enrichment, not primary)
    if (message.includes('Analyzing enrichment data in parallel')) {
      return 'Analyzing enrichment data in parallel';
    }
    
    // Handle intent planning messages (both standalone and combined)
    if (message.includes('Planning to fetch')) {
      // Check if this is part of a combined message with Plan:
      if (message.includes('Plan:')) {
        // Extract planning part from combined message
        const match = message.match(/Planning to fetch (\d+) pieces of knowledge and analyze (\d+) data points/);
        if (match) {
          return `Planning to fetch ${match[1]} pieces of knowledge and analyze ${match[2]} data points`;
        }
      } else {
        // Standalone planning message
        const match = message.match(/Planning to fetch (\d+) pieces of knowledge and analyze (\d+) data points/);
        if (match) {
          return `Planning to fetch ${match[1]} pieces of knowledge and analyze ${match[2]} data points`;
        }
      }
    }
    
    // Handle master query generation
    if (message.includes('Creating consolidated results')) {
      return 'Creating consolidated results from all data requirements';
    }
    
    // Handle visualization stages
    if (message.includes('Deciding visualization configuration')) {
      return 'Deciding visualization configuration';
    }
    
    if (message.includes('Generating final visualization')) {
      return 'Generating final visualization';
    }
    
    // Handle citations stage
    if (message.includes('Adding citations')) {
      return 'Adding citations';
    }
    
    // Handle search enrichment stage - various message formats
    if (message.includes('Enriching with web research') || 
        message.includes('enriching with web search') ||
        message.includes('web search') ||
        message.includes('Citations ready, enriching')) {
      return 'Enriching with web research';
    }
    
    return null;
  };

  // Parse enrichment requirements for expanded view
  const parseEnrichmentRequirements = (message: string): string[] => {
    if (message.includes('Analyzing enrichment data in parallel:')) {
      // Handle both formats: with and without timing suffix like "(15s)"
      const match = message.match(/Analyzing enrichment data in parallel: (.+?)(?:\s+\(\d+s\))?$/);
      if (match) {
        // Split by semicolon and clean up each requirement
        return match[1].split(';').map(req => req.trim()).filter(req => req.length > 0);
      }
    }
    return [];
  };

  const currentRequirement = stageMessage ? parseCurrentRequirement(stageMessage) : null;
  const enrichmentRequirements = stageMessage ? parseEnrichmentRequirements(stageMessage) : [];
  const displayMessage = stageMessage || 'Processing completed.'; // Show completion message when static

  // Determine if we're in enrichment mode
  const isEnrichmentMode = stageMessage?.includes('Analyzing enrichment data in parallel') || false;
  
  // Determine if this is a static (completed) state
  const isStaticState = !stageMessage;

  // Helper function to check if a requirement is a plan
  const isPlanRequirement = (req: string): boolean => {
    // Check if current message contains a plan and this requirement matches the parsed plan
    if (stageMessage?.includes('Plan:')) {
      const planContent = parseCurrentRequirement(stageMessage);
      return planContent === req;
    }
    
    // Check if this requirement was previously tracked as a plan
    return trackedPlans.includes(req);
  };
  
  // Combine current requirement with history for expanded view
  let allRequirements: string[] = [];
  
  // Find plan requirements from history (these go to bottom/oldest)
  const planRequirements = requirementHistory.filter(req => isPlanRequirement(req));
  
  // In static state, show all history; in active state, show current + recent history
  if (isStaticState) {
    // Static state: show complete history in reverse chronological order with plans at bottom
    
    // Start with all non-plan requirements in reverse chronological order (newest first)
    const nonPlanRequirements = requirementHistory
      .filter(req => !planRequirements.includes(req))
      .reverse(); // Most recent first
    
    // Add non-plan requirements first (newest at top)
    allRequirements = [...nonPlanRequirements];
    
    // Add plan requirements last (oldest at bottom)
    if (planRequirements.length > 0) {
      allRequirements = allRequirements.concat(planRequirements);
    }
    
  } else {
    // Active state: show current requirement + enrichment + previous + plans at bottom
    
    // Add current requirement if it exists and is not a plan
    // But skip the general enrichment statement if we have specific enrichment requirements
    if (currentRequirement && !planRequirements.includes(currentRequirement)) {
      // Don't show the general "Analyzing enrichment data in parallel" when we have specific requirements
      const shouldSkipGeneralEnrichment = isEnrichmentMode && 
        enrichmentRequirements.length > 0 && 
        currentRequirement === 'Analyzing enrichment data in parallel';
      
      if (!shouldSkipGeneralEnrichment) {
        allRequirements = [currentRequirement];
      }
    }
    
    // Add enrichment requirements if in enrichment mode
    if (isEnrichmentMode && enrichmentRequirements.length > 0) {
      allRequirements = allRequirements.concat(enrichmentRequirements);
    }
    
    // Add previous requirements in reverse chronological order (excluding current and plans)
    const previousRequirements = requirementHistory
      .filter(req => 
        req !== currentRequirement && 
        !planRequirements.includes(req) &&
        !enrichmentRequirements.includes(req) // Exclude current enrichment requirements to avoid duplication
      )
      .reverse(); // Show in reverse chronological order (most recent first)
    
    allRequirements = allRequirements.concat(previousRequirements);
    
    // Add plan requirements last (at bottom)
    if (planRequirements.length > 0) {
      allRequirements = allRequirements.concat(planRequirements);
    }
  }
  
  // Fallback: if no requirements at all, just show history in reverse order
  if (allRequirements.length === 0 && requirementHistory.length > 0) {
    allRequirements = [...requirementHistory].reverse();
  }

  return (
    <div className="w-full flex justify-center mb-4">
      <div className="w-full sm:w-[95%] md:w-[85%] lg:w-[80%]">
        <div className="bg-white dark:bg-dark-secondary border border-neutral-dark dark:border-dark-border rounded-xl shadow-sm overflow-hidden">
          {/* Header with gradient glow animation */}
          <div 
            className="relative p-4 cursor-pointer select-none"
            onClick={handleToggle}
          >
            {/* Gradient glow animation background - only show when not static */}
            {!isStaticState && (
              <div className="absolute inset-0 opacity-20">
                <div className="h-full w-full bg-gradient-to-r from-transparent via-primary to-transparent animate-gradient-sweep"></div>
              </div>
            )}
            
            {/* Content */}
            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {/* Thinking dots - only animate when not static */}
                <div className="flex space-x-1 flex-shrink-0">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`h-2 w-2 bg-primary rounded-full ${!isStaticState ? 'animate-pulse' : ''}`}
                      style={!isStaticState ? { animationDelay: `${i * 0.2}s` } : {}}
                    />
                  ))}
                </div>
                
                {/* Message */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary dark:text-dark-textPrimary">
                    {displayMessage}
                  </div>
                  {/* {currentStage && (
                    <div className="text-xs text-text-tertiary dark:text-dark-textSecondary mt-1">
                      Stage: {currentStage}
                    </div>
                  )} */}
                </div>
              </div>
              
              {/* Expand/Collapse button */}
              <div className="flex-shrink-0 ml-2">
                <div className="h-5 w-5 text-text-secondary dark:text-dark-textSecondary flex items-center justify-center">
                  {expanded ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
            
            {/* Progress bar if available */}
            {progress !== undefined && progress > 0 && (
              <div className="relative mt-3">
                <div className="flex justify-between text-xs text-text-tertiary dark:text-dark-textSecondary mb-1">
                  <span>Progress</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                  <div 
                    className="bg-primary h-1 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Expandable content */}
          {expanded && allRequirements.length > 0 && (
            <div className="border-t border-neutral-dark dark:border-dark-border bg-neutral-light dark:bg-dark-primary p-4">
              <div className="text-xs font-semibold text-text-secondary dark:text-dark-textSecondary mb-2 uppercase tracking-wide">
                Requirements Progress
              </div>
              <div className="space-y-2">
                {allRequirements.map((req, index) => {
                  // Determine if this is a plan requirement (always stays highlighted)
                  const isPlanReq = isPlanRequirement(req);
                  
                  // In static state, there's no "current" requirement, so treat all non-plans as previous
                  const isCurrentReq = !isStaticState && !isPlanReq && currentRequirement === req;
                  
                  // Determine if this is an active enrichment requirement (part of current enrichment batch)
                  const isActiveEnrichmentReq = !isStaticState && !isPlanReq && !isCurrentReq && isEnrichmentMode && enrichmentRequirements.includes(req);
                  
                  // Everything else is a previous requirement
                  const isPreviousReq = !isPlanReq && !isCurrentReq && !isActiveEnrichmentReq;
                  
                  return (
                    <div key={index} className="flex items-start space-x-2">
                      <div className={`h-2 w-2 rounded-full mt-1.5 flex-shrink-0 ${
                        isPlanReq ? 'bg-blue-600 dark:bg-blue-400' : 
                        isCurrentReq ? `bg-primary ${!isStaticState ? 'animate-pulse' : ''}` : 
                        isActiveEnrichmentReq ? `bg-yellow-500 dark:bg-yellow-400 ${!isStaticState ? 'animate-pulse' : ''}` :
                        'bg-neutral-dark dark:bg-dark-border'
                      }`} />
                      <div className={`text-sm flex-1 ${
                        isPlanReq
                          ? 'text-text-primary dark:text-dark-textPrimary font-medium' 
                          : isCurrentReq 
                          ? 'text-text-primary dark:text-dark-textPrimary font-medium' 
                          : isActiveEnrichmentReq
                          ? 'text-text-primary dark:text-dark-textPrimary font-medium'
                          : 'text-text-secondary dark:text-dark-textSecondary'
                      }`}>
                        {isPlanReq && <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide mr-2">Plan:</span>}
                        {isCurrentReq && <span className="text-xs text-primary dark:text-primary font-semibold uppercase tracking-wide mr-2">Current:</span>}
                        {isActiveEnrichmentReq && <span className="text-xs text-yellow-600 dark:text-yellow-400 font-semibold uppercase tracking-wide mr-2">Enrichment:</span>}
                        {isPreviousReq && <span className="text-xs text-text-tertiary dark:text-dark-textTertiary font-semibold uppercase tracking-wide mr-2">Previous:</span>}
                        {req}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageThinking;
